import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlayCircle, ExternalLink } from "lucide-react";

/**
 * iter-121 — Pinned Getting Started video dialog.
 *
 * Video URL comes from `settings.platform.getting_started_video_url`
 * (configurable by SA via Settings → Platform Policies). YouTube,
 * Vimeo, Loom, MP4 — all auto-embedded based on URL pattern.
 */
export default function GettingStartedVideoDialog({ open, onOpenChange }) {
    const [videoUrl, setVideoUrl] = useState(null);

    useEffect(() => {
        if (!open) return;
        api.get("/auth/signup/policy")  // public, cheap — exposes the URL via settings.platform
            .then(() => api.get("/settings/getting-started-video"))
            .then((r) => setVideoUrl(r.data?.url || ""))
            .catch(() => setVideoUrl(""));
    }, [open]);

    const embedSrc = toEmbed(videoUrl || "");
    const isMp4 = (videoUrl || "").match(/\.(mp4|webm|ogg)(\?.*)?$/i);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="getting-started-video-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <PlayCircle className="h-5 w-5 text-primary" />
                        Getting started
                    </DialogTitle>
                    <DialogDescription>
                        A 3-minute tour of the platform. Bring data, ask Sense AI, ship a dashboard.
                    </DialogDescription>
                </DialogHeader>
                {videoUrl === null && (
                    <div className="aspect-video bg-secondary/40 flex items-center justify-center text-sm text-muted-foreground rounded-sm">
                        Loading…
                    </div>
                )}
                {videoUrl === "" && (
                    <div className="aspect-video bg-secondary/40 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground rounded-sm border border-dashed border-border">
                        <PlayCircle className="h-10 w-10 opacity-30" />
                        <span>No video configured yet.</span>
                        <span className="text-[11px]">SA: set the URL in Settings → Platform Policies.</span>
                    </div>
                )}
                {videoUrl && embedSrc && !isMp4 && (
                    <div className="aspect-video bg-black rounded-sm overflow-hidden">
                        <iframe
                            src={embedSrc}
                            title="Getting started"
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            data-testid="getting-started-iframe"
                        />
                    </div>
                )}
                {videoUrl && isMp4 && (
                    <video
                        src={videoUrl}
                        controls
                        className="w-full aspect-video bg-black rounded-sm"
                        data-testid="getting-started-video-element"
                    />
                )}
                {videoUrl && (
                    <div className="flex justify-end">
                        <a href={videoUrl} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" className="rounded-sm" data-testid="getting-started-open-external">
                                Open original <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                            </Button>
                        </a>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

// Convert a YouTube / Vimeo / Loom URL to its embed URL. Returns null when
// the URL is a direct media file (handled separately with <video>).
function toEmbed(url) {
    if (!url) return null;
    // YouTube
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/);
    if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0&modestbranding=1`;
    // Vimeo
    const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
    // Loom
    const lm = url.match(/loom\.com\/(?:share|embed)\/([0-9a-f]{32,})/i);
    if (lm) return `https://www.loom.com/embed/${lm[1]}`;
    // If it looks like a direct video file, let <video> handle it.
    if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(url)) return null;
    // Fallback: try to embed as iframe (some self-hosted players support it).
    return url;
}
